import { RiFileTextLine, RiImageLine, RiNotionFill } from '@remixicon/react';

import {
  ButtonIcon,
  Menu,
  MenuRoot,
  MenuRow,
} from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';
import {
  GOOGLE_PROVIDER_CONFIG,
  NOTION_PROVIDER_CONFIG,
  useProviderConnect,
} from '@spotify/confidence-auth0-react';
import {
  MenuTriggerPlusIcon,
  useFilePicker,
} from '@spotify/confidence-core-react';
import { ProviderDocSearchDialog } from '@spotify/confidence-external-documents-react';
import { CollaborationV1ExternalDocumentSourceType } from '@spotify/confidence-graphql';
import { useCheckResourcePermissions } from '@spotify/plugin-confidence-permissions-react';

import { ChatAttachment } from '../../types';
import { useAttachProviderDoc } from './useAttachProviderDoc';

interface AttachContextMenuProps {
  onAttached: (attachment: ChatAttachment) => void;
  chatName?: string;
  isDisabled?: boolean;
  onImageFile?: (file: File) => void;
}

export const AttachContextMenu = ({
  onAttached,
  chatName,
  isDisabled,
  onImageFile,
}: AttachContextMenuProps) => {
  const noop = () => {};
  const { open: openImagePicker, fileInputProps: imageInputProps } =
    useFilePicker({ accept: 'image/*', onFile: onImageFile ?? noop });
  const google = useAttachProviderDoc({
    chatName,
    sourceType: CollaborationV1ExternalDocumentSourceType.GoogleDocs,
    onAttached,
  });
  const notion = useAttachProviderDoc({
    chatName,
    sourceType: CollaborationV1ExternalDocumentSourceType.Notion,
    onAttached,
  });

  const {
    isConnected: isGoogleConnected,
    isCheckingConnection: isCheckingGoogleConnection,
    connect: handleConnectGoogle,
  } = useProviderConnect(GOOGLE_PROVIDER_CONFIG, {
    isDialogOpen: google.isDialogOpen,
  });

  const {
    isConnected: isNotionConnected,
    isCheckingConnection: isCheckingNotionConnection,
    connect: handleConnectNotion,
    reconnect: handleReconnectNotion,
  } = useProviderConnect(NOTION_PROVIDER_CONFIG, {
    isDialogOpen: notion.isDialogOpen,
  });

  const isBusy = google.isAttaching || notion.isAttaching;

  const { allowed: canCreateExternalDocument } = useCheckResourcePermissions({
    can: 'create_external_document',
    name: 'account',
    optimistic: false,
  });

  const hasMenu = canCreateExternalDocument || !!onImageFile;

  if (!hasMenu) {
    return (
      <ButtonIcon aria-label="Add context" variant="tertiary" disabled>
        <MenuTriggerPlusIcon />
      </ButtonIcon>
    );
  }

  return (
    <>
      <MenuRoot>
        <ButtonIcon
          aria-label={isBusy ? 'Attaching document' : 'Add context'}
          variant="secondary"
          size="small"
          disabled={isDisabled || isBusy}
          isLoading={isBusy}
        >
          <MenuTriggerPlusIcon />
        </ButtonIcon>
        <Popover hasNoArrow placement="top">
          <Menu aria-label="Add context">
            {canCreateExternalDocument && (
              <>
                <MenuRow
                  textValue="Attach from Google Drive"
                  onAction={google.openDialog}
                 data-track-source-anchor="02692bee29;;low">
                  <RiFileTextLine size={16} />
                  Attach from Google Drive
                </MenuRow>
                <MenuRow
                  textValue="Attach Notion page"
                  onAction={notion.openDialog}
                 data-track-source-anchor="75d73cbf50;;low">
                  <RiNotionFill size={16} />
                  Attach Notion page
                </MenuRow>
              </>
            )}
            {onImageFile && (
              <MenuRow textValue="Attach image" onAction={openImagePicker} data-track-source-anchor="ccb506e118;;low">
                <RiImageLine size={16} />
                Attach image
              </MenuRow>
            )}
          </Menu>
        </Popover>
      </MenuRoot>
      {onImageFile && <input data-track-source-anchor="fd6fbe6fdc" {...imageInputProps} />}
      <ProviderDocSearchDialog
        isOpen={google.isDialogOpen}
        onClose={google.closeDialog}
        onSelect={google.selectPage}
        sourceType={CollaborationV1ExternalDocumentSourceType.GoogleDocs}
        title="Attach from Google Drive"
        searchLabel="Search Google Drive"
        isConnected={isGoogleConnected}
        isCheckingConnection={isCheckingGoogleConnection}
        onConnect={handleConnectGoogle}
        connectLabel="Connect Google account"
       data-track-source-anchor="da23d8e23b"/>
      <ProviderDocSearchDialog
        isOpen={notion.isDialogOpen}
        onClose={notion.closeDialog}
        onSelect={notion.selectPage}
        sourceType={CollaborationV1ExternalDocumentSourceType.Notion}
        title="Attach Notion page"
        searchLabel="Search Notion pages"
        isConnected={isNotionConnected}
        isCheckingConnection={isCheckingNotionConnection}
        onConnect={handleConnectNotion}
        connectLabel="Connect Notion account"
        onReconnect={handleReconnectNotion}
        reconnectLabel="Update Notion access"
       data-track-source-anchor="c8678126e6"/>
    </>
  );
};
